// Widget "Plan a function" panel — enquiry-first.
//
// Direction: weddings / conferences / private events are
// request-to-quote by default. Tenants who've enabled the
// quote-to-pay flow (widget_configs.functions_booking_enabled = true)
// get an additional per-venue "Book this venue" CTA — until PR D/E/F
// ship, that CTA just pre-fills the enquiry form with the venue
// pinned. It's a UX bridge: the customer flow already works as an
// enquiry; the booking flow plugs in later.
//
// Layout:
//   1. Hero
//   2. Venue cards (informational) — name, hero image, capacity,
//      description, function types hosted, menus inline,
//      dietary, cancellation policy. Optional "Book this venue →"
//      CTA per card when booking_enabled.
//   3. Single contact form at the bottom — function type chips,
//      date / start / end / pax / budget, optional preferred venue
//      dropdown, contact info, message.
//   4. Submit → submit_function_booking RPC → confirmation card.
//
// Empty state: when no function venues are configured, the panel
// just renders the hero + a hint pointing the customer toward
// Enquire.

import type React from "react";
import { optimizedImageUrl } from "../lib/image";
// Sales replies by WhatsApp / SMS as well as email, and both need E.164 —
// phone stays optional here (email OR phone), so use the optional variant.
import { normalizeOptionalPhone } from "../lib/phone";
import { useEffect, useMemo, useState, useTransition } from "react";
import type {
  WidgetDefinition,
  WidgetFunctionsCatalogue,
  WidgetLocation,
  WidgetTenant,
  WidgetVenue,
  WidgetVenueFunctionType,
  WidgetVenueMenu,
} from "../types";
import { supabase } from "../config";
import {
  GUEST_BOOKING_CONSENTS,
  LEGAL_DOCUMENT_VERSIONS,
  documentSlugForGuestConsent,
} from "@vertu/shared";
import { recordAttribution } from "../state/attribution";
import {
  consentWasInherited,
  setMarketingConsent,
  trackLead,
} from "../state/tracking";
import { pushHostLead } from "../state/host-datalayer";

type VenueCompatibility = { ok: true } | { ok: false; reason: string };

// Per-event-type capacity (venue_function_types.min_pax/max_pax) takes
// precedence over venue-level capacity. submit_function_booking rejects
// incompatible pairs with `function_type_not_offered`; we want to catch
// that before the round-trip and explain it in plain language.
function venueCompatibility(
  venue: WidgetVenue,
  typeId: string | null,
  partyNum: number | null,
  vfts: WidgetVenueFunctionType[],
  typeName: string | null,
): VenueCompatibility {
  const rangeText = (min: number, max: number | null) =>
    max != null && min > 0
      ? `${min}–${max}`
      : max != null
        ? `up to ${max}`
        : `from ${min}`;

  if (typeId) {
    const vft = vfts.find(
      (x) => x.venue_id === venue.id && x.function_type_id === typeId,
    );
    if (!vft) {
      return {
        ok: false,
        reason: `${venue.name} doesn't host ${typeName ?? "this event type"}.`,
      };
    }
    if (partyNum != null) {
      const min = vft.min_pax ?? venue.capacity_min ?? 0;
      const max = vft.max_pax ?? venue.capacity_max;
      if (partyNum < min || (max != null && partyNum > max)) {
        return {
          ok: false,
          reason: `${venue.name} fits ${rangeText(min, max)} guests for ${typeName ?? "this event"}.`,
        };
      }
    }
    return { ok: true };
  }

  if (partyNum != null) {
    const min = venue.capacity_min ?? 0;
    const max = venue.capacity_max;
    if (partyNum < min || (max != null && partyNum > max)) {
      return {
        ok: false,
        reason: `${venue.name} fits ${rangeText(min, max)} guests.`,
      };
    }
  }
  return { ok: true };
}

export function FunctionsPanel({
  tenant,
  functions,
  widget,
  initialFunctionTypeId = null,
  onConsumeInitialFunctionType,
}: {
  tenant: WidgetTenant;
  functions: WidgetFunctionsCatalogue;
  // widget is optional so older callers (legacy bundle vs. new admin)
  // keep working — when absent we render the historical hardcoded copy.
  widget?: Pick<
    WidgetDefinition,
    | "functions_tab_label"
    | "functions_tab_tagline"
    | "functions_tab_description"
    | "functions_tab_hero_image_url"
  >;
  /**
   * Pre-selected function type chip when the customer arrived from a
   * function_types_list panel row. Consumed once on mount via the
   * onConsumeInitialFunctionType callback so a return-trip-back to the
   * tab doesn't keep re-selecting it.
   */
  initialFunctionTypeId?: string | null;
  onConsumeInitialFunctionType?: () => void;
}) {
  // Resolve tenant-set overrides with sensible defaults. Trim guards
  // against admins saving a single space and getting an "empty" hero.
  const heroHeading =
    widget?.functions_tab_label?.trim() || "Host an event with us";
  const heroTagline =
    widget?.functions_tab_tagline?.trim() ||
    "Weddings, conferences, private events. Browse our venues below, then send a request — we'll reply with a tailored quote within one business day.";
  const heroDescription = widget?.functions_tab_description?.trim() || null;
  const heroImageUrl = widget?.functions_tab_hero_image_url?.trim() || null;
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ reference: string } | null>(null);

  // Form state. preferredVenueId is OPTIONAL — submitting without it
  // is fine; the row goes into function_bookings with venue_id = null
  // and sales picks the right venue when they reply.
  const [preferredVenueId, setPreferredVenueId] = useState<string | null>(null);
  const [functionTypeId, setFunctionTypeId] = useState<string | null>(
    initialFunctionTypeId,
  );
  // Catch the case where the customer is already on the Functions tab
  // (panel mounted with no initial) and then clicks a Plan-a-Wedding
  // row in the footer. We need to re-sync the chip from the new prop.
  useEffect(() => {
    if (initialFunctionTypeId) {
      setFunctionTypeId(initialFunctionTypeId);
      onConsumeInitialFunctionType?.();
    }
    // We deliberately don't depend on onConsumeInitialFunctionType — it
    // changes every render in the parent, which would refire the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFunctionTypeId]);
  const [eventDate, setEventDate] = useState<string>("");
  const [startTime, setStartTime] = useState<string>("");
  const [endTime, setEndTime] = useState<string>("");
  const [partySize, setPartySize] = useState<string>("");
  const [budget, setBudget] = useState<string>("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactPhoneError, setContactPhoneError] = useState<string | null>(null);
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");
  // POPIA marketing opt-in (unticked by default, unless inherited from the
  // tenant's own site). Gates the browser Lead pixel and the server-side lead
  // conversion, same as EnquiryPanel.
  const [consentMarketing, setConsentMarketing] = useState(() =>
    consentWasInherited(),
  );

  // Derived state — hoisted above submit() because submit references
  // pinReason/partyNum/selectedTypeMeta for pre-flight validation.
  const selectedTypeMeta = useMemo(
    () => functions.function_types.find((ft) => ft.id === functionTypeId) ?? null,
    [functions.function_types, functionTypeId],
  );
  const partyNum = useMemo(() => {
    if (!partySize) return null;
    const n = Number(partySize);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [partySize]);
  // Only show event types that AT LEAST ONE venue actually hosts.
  // An admin can configure a function type without yet linking it to
  // any venue via venue_function_types — in that case the chip would
  // look offered but every venue would reject it. Hide it instead.
  const offeredFunctionTypes = useMemo(() => {
    const hostedIds = new Set(
      functions.venue_function_types.map((vft) => vft.function_type_id),
    );
    return functions.function_types.filter((ft) => hostedIds.has(ft.id));
  }, [functions.function_types, functions.venue_function_types]);

  const pinReason = useMemo(() => {
    if (!preferredVenueId) return null;
    const v = functions.venues.find((x) => x.id === preferredVenueId);
    if (!v) return null;
    const c = venueCompatibility(
      v,
      functionTypeId,
      partyNum,
      functions.venue_function_types,
      selectedTypeMeta?.name ?? null,
    );
    return c.ok ? null : c.reason;
  }, [
    preferredVenueId,
    functionTypeId,
    partyNum,
    functions.venues,
    functions.venue_function_types,
    selectedTypeMeta,
  ]);

  // We submit through submit_function_booking even though this is an
  // enquiry — the schema's `submitted` status IS the enquiry stage,
  // and once booking_enabled flips to true the same row carries
  // forward into the quote-to-pay pipeline without a separate
  // submit path.
  function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);

    if (!functionTypeId) {
      // Need at least a function type so sales knows what they're
      // quoting. The chip row handles selection up-front so this is
      // mostly a safety net.
      return setError("Pick a function type so we know what to quote.");
    }
    if (!contactName.trim()) return setError("Your name is required.");
    if (!contactEmail.trim() && !contactPhone.trim()) {
      return setError("Add an email or phone so we can reply.");
    }
    // Store the number the way we'll message it. An un-dialable number looks
    // contactable in the sales inbox and silently drops the WhatsApp/SMS reply.
    const phoneNorm = normalizeOptionalPhone(contactPhone);
    if (!phoneNorm.ok) {
      setContactPhoneError(phoneNorm.error ?? null);
      return setError(phoneNorm.error ?? "Check the phone number.");
    }
    setContactPhoneError(null);
    if (phoneNorm.e164 && phoneNorm.e164 !== contactPhone) {
      setContactPhone(phoneNorm.e164);
    }
    if (!eventDate) return setError("Pick an event date — sales needs it to quote correctly.");
    // Guest count drives venue compatibility — without it we can't tell
    // which venues will actually fit the party, and the customer can't
    // get a meaningful quote. Required even though the RPC accepts null.
    if (partyNum == null) {
      return setError("Tell us how many guests so we can match the right venue.");
    }

    // Block the round-trip if the customer has pinned a venue that
    // doesn't host their event type or can't fit their party. The RPC
    // would otherwise reject with `function_type_not_offered`.
    if (preferredVenueId && pinReason) {
      return setError(pinReason);
    }

    // submit_function_booking requires venue_id NOT NULL (per PR #78
    // schema). When the customer hasn't picked a preferred venue, fall
    // back to the first venue that ACTUALLY hosts the selected event
    // type (and fits the party) — picking the first venue alphabetically
    // could land on one that doesn't, which the RPC rejects.
    const venueIdForRpc = (() => {
      if (preferredVenueId) return preferredVenueId;
      const match = functions.venues.find((v) =>
        venueCompatibility(
          v,
          functionTypeId,
          partyNum,
          functions.venue_function_types,
          selectedTypeMeta?.name ?? null,
        ).ok,
      );
      return match?.id ?? null;
    })();
    if (!venueIdForRpc) {
      // No venue can host this combination — surface the actual reason.
      return setError(
        partyNum != null
          ? `No venue hosts ${selectedTypeMeta?.name ?? "this event type"} for ${partyNum} guests. Try a different event type or guest count.`
          : `No venue hosts ${selectedTypeMeta?.name ?? "this event type"}. Pick a different event type.`,
      );
    }

    startTransition(async () => {
      const paxNum = partySize ? Number(partySize) : null;
      const { data, error: rpcErr } = await supabase.rpc(
        "submit_function_booking",
        {
          p_tenant_id: tenant.id,
          p_venue_id: venueIdForRpc,
          p_function_type_id: functionTypeId,
          p_contact_name: contactName.trim(),
          p_contact_email: contactEmail.trim() || null,
          p_contact_phone: phoneNorm.e164,
          p_company: company.trim() || null,
          p_event_date: eventDate,
          p_event_start_time: startTime || null,
          p_event_end_time: endTime || null,
          p_party_size: paxNum && Number.isFinite(paxNum) ? paxNum : null,
          p_preferred_menu_id: null,
          // Clamped to int4 — an over-large budget overflowed the RPC's
          // integer param (22003) and silently lost the enquiry.
          p_budget_cents_approx:
            budget && Number.isFinite(parseFloat(budget))
              ? Math.min(Math.round(parseFloat(budget) * 100), 2_000_000_000)
              : null,
          p_message: message.trim() || null,
          p_referrer_url:
            typeof window !== "undefined" ? window.location.href : null,
        },
      );
      if (rpcErr) return setError(rpcErr.message);
      const resp = data as
        | {
            ok?: boolean;
            reason?: string;
            reference?: string;
            enquiry_id?: string;
          }
        | null;
      if (!resp?.ok) {
        return setError(resp?.reason ?? "Couldn't submit. Please try again.");
      }
      // Marketing consent + lead-conversion tracking (mirrors EnquiryPanel).
      // Record consent BEFORE attribution so the server-side lead enqueue that
      // the attribution write triggers sees the committed consent_log row.
      setMarketingConsent(consentMarketing);
      const leadEmail = contactEmail.trim();
      if (consentMarketing && leadEmail) {
        try {
          const slug = documentSlugForGuestConsent("marketing_optin");
          await supabase.rpc("log_consent_anon", {
            p_email: leadEmail,
            p_tenant_id: tenant.id,
            p_document_slug: slug,
            p_document_version: LEGAL_DOCUMENT_VERSIONS[slug],
            p_checkbox_text: GUEST_BOOKING_CONSENTS.marketing_optin.text,
            p_consent_kind: GUEST_BOOKING_CONSENTS.marketing_optin.kind,
            p_granted: true,
            p_metadata: { source: "widget_function_enquiry" },
          });
        } catch {
          // Best-effort — never block the enquiry on a consent-log write.
        }
      }
      // Attribution write ALSO fires the server-side lead conversion (trigger on
      // enquiries.campaign_metadata).
      if (resp.enquiry_id) {
        void recordAttribution({ enquiryId: resp.enquiry_id });
      }
      // Browser-side lead conversion; eventId = enquiry id dedupes vs the
      // server CAPI Lead.
      trackLead({
        value: budget ? parseFloat(budget) : undefined,
        currency: tenant.currency ?? "ZAR",
        eventId: resp.enquiry_id,
      });
      // The venue's own tags (an embed on its site), through its dataLayer:
      // never personal data (state/host-datalayer.ts).
      pushHostLead({
        tenant: tenant.slug,
        enquiryId: resp.enquiry_id,
        value: budget ? parseFloat(budget) : null,
        currency: tenant.currency ?? "ZAR",
      });
      setSuccess({ reference: resp.reference ?? "" });
    });
  }

  // ─── Empty state ─────────────────────────────────────────────────
  if (functions.venues.length === 0) {
    return (
      <section className="vt-panel vt-functions-panel">
        <div className="vt-service-card">
          <div
            className="vt-hero"
            style={
              heroImageUrl
                ? { backgroundImage: `url(${optimizedImageUrl(heroImageUrl, { width: 1280 })})`, backgroundSize: "cover", backgroundPosition: "center" }
                : undefined
            }
          >
            {!heroImageUrl && <div className="vt-hero-gradient vt-hero-enquiry" />}
            <div className="vt-hero-overlay">
              <div className="vt-hero-overlay-text">
                <h2>{heroHeading}</h2>
                <p className="vt-hero-tag">{heroTagline}</p>
              </div>
            </div>
          </div>
          <div className="vt-service-body">
            <p className="vt-muted">
              We haven&apos;t opened any function venues for online enquiries
              yet. Use the <strong>Enquire</strong> tab and we&apos;ll connect
              you with our events team.
            </p>
          </div>
        </div>
      </section>
    );
  }

  // ─── Confirmation ────────────────────────────────────────────────
  if (success) {
    return (
      <section className="vt-panel vt-functions-panel">
        <div className="vt-confirmed">
          <div className="vt-check">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={3}
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h3>Request received</h3>
          <p className="vt-muted">
            Thanks {contactName.trim() || "for getting in touch"}. Our team
            will follow up with a quote within one business day.
          </p>
          {success.reference && (
            <p className="vt-muted" style={{ marginTop: 8, fontSize: 12 }}>
              Reference: <code>{success.reference}</code>
            </p>
          )}
        </div>
      </section>
    );
  }

  // ─── Main render ─────────────────────────────────────────────────

  return (
    <section className="vt-panel vt-functions-panel">
      <div className="vt-service-card">
        <div
          className="vt-hero"
          style={
            heroImageUrl
              ? { backgroundImage: `url(${heroImageUrl})`, backgroundSize: "cover", backgroundPosition: "center" }
              : undefined
          }
        >
          {!heroImageUrl && <div className="vt-hero-gradient vt-hero-enquiry" />}
          <div className="vt-hero-overlay">
            <div className="vt-hero-overlay-text">
              <h2>{heroHeading}</h2>
              <p className="vt-hero-tag">{heroTagline}</p>
            </div>
          </div>
        </div>

        <div className="vt-service-body">
          {heroDescription && (
            <p
              className="vt-muted vt-functions-tab-description"
              style={{ marginBottom: 16, lineHeight: 1.5 }}
            >
              {heroDescription}
            </p>
          )}
          {/* Guest count drives venue compatibility — show the prompt
              ABOVE the venue cards so the customer answers it before
              browsing. Bound to the same partySize state as the form
              below; either input updates both. */}
          <div className="vt-functions-prefilter">
            <div className="vt-functions-prefilter-row">
              <label
                htmlFor="vt-functions-guest-prefilter"
                className="vt-functions-prefilter-q"
              >
                How many guests are you expecting?
              </label>
              <input
                id="vt-functions-guest-prefilter"
                type="number"
                min="1"
                value={partySize}
                onChange={(e) => setPartySize(e.target.value)}
                placeholder="e.g. 60"
              />
              {partyNum != null && (
                <span className="vt-muted" style={{ fontSize: 12 }}>
                  Showing venues that fit {partyNum} guests
                  {selectedTypeMeta ? ` for ${selectedTypeMeta.name}` : ""}.
                </span>
              )}
            </div>
          </div>
          {/* Venue gallery — informational. No required pick; the
              optional "Preferred venue" dropdown in the form is the
              way a customer pins their preference. */}
          <div className="vt-functions-venue-list">
            {functions.venues.map((v) => {
              const compat = venueCompatibility(
                v,
                functionTypeId,
                partyNum,
                functions.venue_function_types,
                selectedTypeMeta?.name ?? null,
              );
              return (
                <VenueCard
                  key={v.id}
                  venue={v}
                  location={functions.locations.find((l) => l.id === v.location_id) ?? null}
                  functions={functions}
                  bookingEnabled={functions.booking_enabled}
                  isPinned={preferredVenueId === v.id}
                  compatibility={compat}
                  onPin={() => {
                    // "Book this venue" + "Send enquiry about this venue"
                    // both route to the same outcome for now — preferred
                    // venue gets pinned and the form scrolls into view.
                    // When booking_enabled wires through to PR D/E/F's
                    // quote-to-pay flow, this CTA will kick off that
                    // path instead.
                    setPreferredVenueId(v.id);
                    // If the customer hasn't picked an event type yet and
                    // this venue hosts exactly ONE, pre-select it — otherwise
                    // pinning the venue dead-ends at the "pick a function
                    // type" guard on submit (the reported deep-link bug).
                    if (!functionTypeId) {
                      const hosted = Array.from(
                        new Set(
                          functions.venue_function_types
                            .filter((vft) => vft.venue_id === v.id)
                            .map((vft) => vft.function_type_id),
                        ),
                      ).filter((id) =>
                        offeredFunctionTypes.some((ft) => ft.id === id),
                      );
                      const soleType = hosted.length === 1 ? hosted[0] : undefined;
                      if (soleType) setFunctionTypeId(soleType);
                    }
                    if (typeof document !== "undefined") {
                      document
                        .getElementById("vt-functions-enquiry-form")
                        ?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }
                  }}
                />
              );
            })}
          </div>

          {/* Enquiry form. Single submit regardless of which venue (if
              any) the customer pinned. */}
          <form
            id="vt-functions-enquiry-form"
            className="vt-checkout vt-functions-form"
            onSubmit={submit}
          >
            <div>
              <div className="vt-section-label">Your enquiry</div>
            </div>

            {offeredFunctionTypes.length > 0 ? (
              <div>
                <div className="vt-section-label">What kind of event?</div>
                <div className="vt-dine-chip-row">
                  {offeredFunctionTypes.map((ft) => (
                    <button
                      key={ft.id}
                      type="button"
                      className={`vt-chip ${ft.id === functionTypeId ? "vt-chip-active" : ""}`}
                      onClick={() => setFunctionTypeId(ft.id)}
                    >
                      {ft.name}
                    </button>
                  ))}
                </div>
                {selectedTypeMeta?.suggested_lead_days != null && (
                  <p
                    className="vt-muted"
                    style={{ fontSize: 12, marginTop: 6 }}
                  >
                    Suggested lead time: {selectedTypeMeta.suggested_lead_days}+
                    days.
                  </p>
                )}
              </div>
            ) : functions.function_types.length > 0 ? (
              // Tenant has function types configured but none are linked to
              // a venue yet (no venue_function_types rows). Avoid showing
              // chips that no venue can host — every submit would be
              // rejected. Send the customer to the generic Enquire tab.
              <div>
                <div className="vt-section-label">What kind of event?</div>
                <p className="vt-muted" style={{ fontSize: 13 }}>
                  Our event types aren&apos;t open for online enquiries yet.
                  Use the <strong>Enquire</strong> tab and our team will be in
                  touch.
                </p>
              </div>
            ) : null}

            <div className="vt-form-row-3">
              <div className="vt-field">
                <label>Event date</label>
                <input
                  type="date"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                  required
                />
              </div>
              <div className="vt-field">
                <label>Start time</label>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                />
              </div>
              <div className="vt-field">
                <label>End time</label>
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                />
              </div>
            </div>

            <div className="vt-form-row">
              <div className="vt-field">
                <label>Number of guests</label>
                <input
                  type="number"
                  min="1"
                  value={partySize}
                  onChange={(e) => setPartySize(e.target.value)}
                  required
                  aria-required="true"
                />
              </div>
              <div className="vt-field">
                <label>Approx budget ({tenant.currency ?? "ZAR"}, optional)</label>
                <input
                  type="number"
                  min="0"
                  step="100"
                  value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                />
              </div>
            </div>

            <div className="vt-field vt-field-chevron">
              <label>Preferred venue (optional)</label>
              <select
                value={preferredVenueId ?? ""}
                onChange={(e) =>
                  setPreferredVenueId(e.target.value || null)
                }
              >
                <option value="">No preference</option>
                {functions.venues.map((v) => {
                  const loc = functions.locations.find(
                    (l) => l.id === v.location_id,
                  );
                  const locLabel = loc?.city ?? loc?.name ?? null;
                  const compat = venueCompatibility(
                    v,
                    functionTypeId,
                    partyNum,
                    functions.venue_function_types,
                    selectedTypeMeta?.name ?? null,
                  );
                  const base = locLabel ? `${v.name} · ${locLabel}` : v.name;
                  return (
                    <option key={v.id} value={v.id} disabled={!compat.ok}>
                      {compat.ok ? base : `${base} — not available`}
                    </option>
                  );
                })}
              </select>
              {pinReason && (
                <p
                  className="vt-muted"
                  style={{
                    fontSize: 12,
                    marginTop: 6,
                    paddingInline: 12,
                    color: "#C0392B",
                  }}
                >
                  {pinReason} Choose another venue, or change your event type
                  or guest count.
                </p>
              )}
            </div>

            <div className="vt-form-row">
              <div className="vt-field">
                <label>Your name</label>
                <input
                  type="text"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  required
                />
              </div>
              <div className="vt-field">
                <label>Company (optional)</label>
                <input
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                />
              </div>
            </div>

            <div className="vt-form-row">
              <div className="vt-field">
                <label>Email</label>
                <input
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                />
              </div>
              <div className="vt-field">
                <label>Phone</label>
                <input
                  type="tel"
                  placeholder="+27 82 123 4567"
                  value={contactPhone}
                  onChange={(e) => {
                    setContactPhone(e.target.value);
                    if (contactPhoneError) setContactPhoneError(null);
                  }}
                  onBlur={() => {
                    if (!contactPhone.trim()) return setContactPhoneError(null);
                    const check = normalizeOptionalPhone(contactPhone);
                    if (check.ok) {
                      if (check.e164) setContactPhone(check.e164);
                      setContactPhoneError(null);
                    } else {
                      setContactPhoneError(check.error ?? null);
                    }
                  }}
                  aria-invalid={contactPhoneError != null}
                />
                {contactPhoneError && (
                  <div className="vt-field-error" role="alert">
                    {contactPhoneError}
                  </div>
                )}
              </div>
            </div>

            <div className="vt-field">
              <label>Tell us more</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                placeholder="Theme, style, catering preferences, AV needs, anything else."
              />
            </div>

            <label className="vt-consent-row">
              <input
                type="checkbox"
                checked={consentMarketing}
                onChange={(e) => setConsentMarketing(e.target.checked)}
              />
              <span>{GUEST_BOOKING_CONSENTS.marketing_optin.text}</span>
            </label>

            {error && <div className="vt-capacity-error">{error}</div>}

            <button
              type="submit"
              className="vt-cta vt-cta-success"
              disabled={pending}
            >
              {pending ? "Sending…" : "Send request"}
            </button>

            <p className="vt-muted vt-text-center" style={{ fontSize: 11 }}>
              No payment now — our team replies with a tailored quote within
              one business day.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}

// ─── Venue card ─────────────────────────────────────────────────────

function VenueCard({
  venue,
  location,
  functions,
  bookingEnabled,
  isPinned,
  compatibility,
  onPin,
}: {
  venue: WidgetVenue;
  location: WidgetLocation | null;
  functions: WidgetFunctionsCatalogue;
  bookingEnabled: boolean;
  isPinned: boolean;
  compatibility: VenueCompatibility;
  onPin: () => void;
}) {
  // Function types this venue hosts (via venue_function_types m2m).
  const venueFunctionTypes: WidgetVenueFunctionType[] = useMemo(
    () =>
      functions.venue_function_types.filter((vft) => vft.venue_id === venue.id),
    [functions.venue_function_types, venue.id],
  );
  const offeredTypes = useMemo(() => {
    const ids = new Set(venueFunctionTypes.map((vft) => vft.function_type_id));
    return functions.function_types.filter((ft) => ids.has(ft.id));
  }, [venueFunctionTypes, functions.function_types]);

  // Menus offered at this venue (any-function + venue-scoped).
  const venueMenus: WidgetVenueMenu[] = useMemo(
    () => functions.menus.filter((m) => m.venue_id === venue.id),
    [functions.menus, venue.id],
  );

  const capacityText = (() => {
    if (venue.capacity_min != null && venue.capacity_max != null) {
      return `${venue.capacity_min}–${venue.capacity_max} guests`;
    }
    if (venue.capacity_max != null) return `Up to ${venue.capacity_max} guests`;
    if (venue.capacity_min != null) return `From ${venue.capacity_min} guests`;
    return null;
  })();

  const compatibleNo = !compatibility.ok;
  return (
    <article
      className={`vt-functions-venue-info${isPinned ? " vt-functions-venue-info-pinned" : ""}${compatibleNo ? " vt-functions-venue-info-unavailable" : ""}`}
    >
      {/* Always render the hero slot so the 2-col grid stays balanced.
          When no image is set, show a soft branded placeholder with the
          venue's initial — keeps the card from looking lopsided before
          tenants upload imagery. */}
      <div
        className="vt-functions-venue-info-hero"
        style={
          venue.hero_image_url
            ? { backgroundImage: `url(${optimizedImageUrl(venue.hero_image_url, { width: 1280 })})` }
            : undefined
        }
      >
        {!venue.hero_image_url && (
          <div className="vt-functions-venue-info-hero-placeholder" aria-hidden>
            {venue.name.charAt(0).toUpperCase()}
          </div>
        )}
        {/* Title + the function types this venue hosts sit over the image
            (frosted chips + gradient scrim) so the card stays compact and
            the imagery does the heavy lifting. */}
        <div className="vt-functions-venue-info-hero-overlay">
          <h3 className="vt-functions-venue-info-name">{venue.name}</h3>
          {offeredTypes.length > 0 && (
            <div className="vt-functions-venue-info-tags">
              {offeredTypes.map((ft) => (
                <span key={ft.id} className="vt-functions-venue-info-tag">
                  {ft.name}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="vt-functions-venue-info-body">
        <div className="vt-functions-venue-info-head">
          {(location || capacityText || venue.indoor || venue.accessible) && (
            <p className="vt-functions-venue-info-meta">
              {[
                location ? `at ${location.name}${location.city ? ` · ${location.city}` : ""}` : null,
                capacityText,
                venue.indoor ? "Indoor" : "Outdoor",
                venue.accessible ? "Wheelchair accessible" : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          )}
          {venue.tagline && (
            <p className="vt-functions-venue-info-tagline">{venue.tagline}</p>
          )}
        </div>

        {venue.description && (
          <p className="vt-functions-venue-info-desc">{venue.description}</p>
        )}

        {venueMenus.length > 0 && (
          <div className="vt-functions-venue-info-section">
            <span className="vt-functions-venue-info-section-label">Menus</span>
            <ul className="vt-functions-venue-info-menus">
              {venueMenus.map((m) => {
                const courseCount = m.courses.length;
                const hasDetail =
                  courseCount > 0 || (m.dietary_notes?.trim().length ?? 0) > 0;
                return (
                  <li key={m.id} className="vt-functions-venue-info-menu">
                    <span className="vt-functions-venue-info-menu-name">
                      {m.name}
                    </span>
                    <span className="vt-functions-venue-info-menu-price">
                      {m.currency}{" "}
                      {(m.price_per_head_cents / 100).toLocaleString()} per head
                    </span>
                    {m.description && (
                      <span className="vt-functions-venue-info-menu-desc">
                        {m.description}
                      </span>
                    )}
                    {hasDetail && (
                      <details className="vt-functions-venue-info-menu-detail">
                        <summary>
                          {courseCount > 0
                            ? `View ${courseCount} ${courseCount === 1 ? "course" : "courses"}`
                            : "Dietary notes"}
                        </summary>
                        {courseCount > 0 && (
                          <ol className="vt-functions-venue-info-menu-courses-list">
                            {m.courses.map((c, idx) => (
                              <li key={`${m.id}-${idx}`}>
                                <span className="vt-functions-venue-info-menu-course-name">
                                  {c.name}
                                </span>
                                {c.items.length > 0 && (
                                  <ul className="vt-functions-venue-info-menu-course-items">
                                    {c.items.map((item, i) => (
                                      <li key={`${m.id}-${idx}-${i}`}>
                                        {item}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </li>
                            ))}
                          </ol>
                        )}
                        {m.dietary_notes && (
                          <p className="vt-functions-venue-info-menu-dietary">
                            <strong>Dietary:</strong> {m.dietary_notes}
                          </p>
                        )}
                      </details>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {venue.cancellation_policy_text && (
          <p className="vt-functions-venue-info-policy">
            {venue.cancellation_policy_text}
          </p>
        )}

        {compatibleNo && (
          <p
            className="vt-functions-venue-info-warning"
            style={{
              fontSize: 12,
              color: "#C0392B",
              marginTop: 8,
              marginBottom: 0,
            }}
          >
            {compatibility.reason}
          </p>
        )}

        <div className="vt-functions-venue-info-actions">
          <button
            type="button"
            className="vt-cta-inline"
            onClick={onPin}
            disabled={compatibleNo}
            aria-disabled={compatibleNo}
            title={compatibleNo ? compatibility.reason : undefined}
          >
            {compatibleNo
              ? "Not available for this enquiry"
              : bookingEnabled
                ? isPinned
                  ? "Selected · scroll to enquire"
                  : "Book this venue →"
                : isPinned
                  ? "Selected as your preferred venue ↓"
                  : "Enquire about this venue →"}
          </button>
        </div>
      </div>
    </article>
  );
}
